import { useNavigate } from '@tanstack/react-router';
import { ArrowUpRightIcon, PlusIcon } from '@phosphor-icons/react';
import { type CardId, deriveTaskDisplayStatus, type TaskWithCard } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { useCardDetail } from '@web/ui/core';
import { Button, Card, TaskStatusIcon } from '@web/ui/core/atoms';

import { CreateTaskForm } from '../components/create_task_form';
import { useCardRoutes } from '../hooks/use_card_routes';
import { useCardTasks } from '../hooks/use_card_tasks';
import { useCreateTask } from '../hooks/use_create_task';
import { fireAndForget } from '@web/ui/core/utils';

export interface TasksViewProps {
  cardId: CardId;
  /** Highlights the open task when the task detail panel is showing. */
  activeTaskId?: string;
}

/**
 * Tasks tab. Renders the tasks attached to the card. Each task row opens the
 * task detail drawer.
 *
 * Fields the v2 mockup shows but the schema does not carry yet — recovery
 * flag, lock/lease, handoff stack — are intentionally omitted.
 */
export function TasksView({ cardId, activeTaskId }: Readonly<TasksViewProps>): React.JSX.Element {
  const navigate = useNavigate();
  const routes = useCardRoutes();
  const detailQuery = useCardDetail(cardId);
  const { query, tasks } = useCardTasks(cardId);
  const createTask = useCreateTask(cardId);

  const openTask = (taskId: string): void => {
    fireAndForget(navigate(routes.task(taskId)));
  };

  let tasksContent: React.ReactNode;
  if (query.isLoading) {
    tasksContent = (
      <p className="text-meta text-muted-foreground">{t('board.drawer.tasks.loading')}</p>
    );
  } else if (query.isError || !query.data) {
    tasksContent = <p className="text-meta text-tone-red-fg">{t('board.drawer.tasks.error')}</p>;
  } else if (tasks.length === 0) {
    tasksContent = (
      <p
        data-testid="drawer-tasks-empty"
        className="text-meta text-faint-foreground text-center py-6"
      >
        {t('board.drawer.tasks.empty')}
      </p>
    );
  } else {
    tasksContent = (
      <ul data-testid="drawer-tasks-list" className="flex flex-col gap-3 list-none m-0 p-0">
        {tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            active={task.id === activeTaskId}
            onOpen={() => {
              openTask(task.id);
            }}
          />
        ))}
      </ul>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-mono text-tiny uppercase tracking-eyebrow text-muted-foreground">
          {t('board.drawer.tasks.sectionCount', { count: tasks.length })}
        </span>
        {detailQuery.data?.closure === null && !createTask.isOpen ? (
          <Button variant="ghost" size="xs" onClick={createTask.open}>
            <PlusIcon size={14} weight="bold" aria-hidden />
            {t('board.drawer.tasks.create')}
          </Button>
        ) : null}
      </div>

      {createTask.isOpen ? (
        <CreateTaskForm
          onSubmit={createTask.create}
          pending={createTask.pending}
          onCancel={createTask.cancel}
        />
      ) : null}

      {tasksContent}
    </section>
  );
}

function TaskRow({
  task,
  active,
  onOpen,
}: Readonly<{
  task: TaskWithCard;
  active?: boolean;
  onOpen: () => void;
}>): React.JSX.Element {
  return (
    <Card active={active} aria-label={task.description}>
      <button
        type="button"
        onClick={onOpen}
        className="group flex w-full items-center gap-2 px-3.5 py-3 text-left"
        data-task-id={task.id}
      >
        <TaskStatusIcon status={deriveTaskDisplayStatus(task)} size={16} />
        <span className="flex-1 text-base font-medium text-foreground truncate">
          {task.description}
        </span>
        <span className="font-mono text-card-id text-muted-foreground tabular-nums whitespace-nowrap">
          T-{task.id.slice(0, 6)}
        </span>
        <ArrowUpRightIcon
          size={13}
          aria-hidden
          className="text-faint-foreground group-hover:text-foreground"
        />
      </button>
      {task.status === 'failed' && task.errorCode ? (
        <p className="m-0 px-3.5 pb-3 text-meta text-tone-red-fg">
          {t('board.drawer.tasks.errorCode', { code: task.errorCode })}
        </p>
      ) : null}
    </Card>
  );
}
